import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  companyCreateSchema,
  companyUpdateSchema,
  type CompanySummary,
  type CompanyCreateInput,
  type CompanyUpdateInput,
} from '@ventin/shared';
import { useCreateCompany, useUpdateCompany } from '@/lib/adminApi';

type Mode = 'create' | 'edit';

interface Props {
  mode: Mode;
  initial?: CompanySummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type FieldErrors = Partial<Record<string, string>> & { form?: string };

/** American time zones only (owner spec 2026-06-11) */
const US_TIMEZONES = [
  { tz: 'America/New_York', label: 'Eastern (ET)' },
  { tz: 'America/Chicago', label: 'Central (CT)' },
  { tz: 'America/Denver', label: 'Mountain (MT)' },
  { tz: 'America/Phoenix', label: 'Arizona (no DST)' },
  { tz: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { tz: 'America/Anchorage', label: 'Alaska (AKT)' },
  { tz: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
];

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '')
    .slice(0, 40);
}

/**
 * CompanyFormDialog — create/edit a Company.
 *
 * Create (owner spec 2026-06-11) is intentionally minimal: friendly business
 * name + main email + main phone. The slug is auto-derived from the name and
 * everything else is filled out inside the company profile after creation.
 * Edit exposes the full business profile.
 */
export function CompanyFormDialog({ mode, initial, open, onOpenChange }: Props) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [gaOverride, setGaOverride] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [legalName, setLegalName] = useState('');
  const [ein, setEin] = useState('');
  const [website, setWebsite] = useState('');
  const [niche, setNiche] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [timezone, setTimezone] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);

  const createMut = useCreateCompany();
  const updateMut = useUpdateCompany();

  // Re-sync form state whenever the dialog opens (or the initial target changes).
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSlugTouched(false);
    if (mode === 'edit' && initial) {
      setName(initial.name);
      setSlug(initial.slug);
      setGaOverride(initial.gaPropertyIdOverride ?? '');
      setEmail(initial.email ?? '');
      setPhone(initial.phone ?? '');
      setLegalName(initial.legalName ?? '');
      setEin(initial.ein ?? '');
      setWebsite(initial.website ?? '');
      setNiche(initial.niche ?? '');
      setAddressLine1(initial.addressLine1 ?? '');
      setAddressLine2(initial.addressLine2 ?? '');
      setCity(initial.city ?? '');
      setState(initial.state ?? '');
      setZip(initial.zip ?? '');
      setTimezone(initial.timezone ?? '');
    } else {
      setName('');
      setSlug('');
      setGaOverride('');
      setEmail('');
      setPhone('');
      setLegalName('');
      setEin('');
      setWebsite('');
      setNiche('');
      setAddressLine1('');
      setAddressLine2('');
      setCity('');
      setState('');
      setZip('');
      setTimezone('');
    }
  }, [open, mode, initial]);

  // In create mode the slug tracks the name until manually edited.
  useEffect(() => {
    if (mode === 'create' && !slugTouched) setSlug(slugify(name));
  }, [name, mode, slugTouched]);

  /** '' → omit on create; '' → null on edit (clears the stored value). */
  function opt(value: string): string | null | undefined {
    const v = value.trim();
    if (v) return v;
    return mode === 'edit' ? null : undefined;
  }

  function buildPayload(): { input: CompanyCreateInput | CompanyUpdateInput; errs: FieldErrors } {
    const raw: Record<string, unknown> = { name, slug };
    if (gaOverride.trim()) raw.gaPropertyIdOverride = gaOverride.trim();
    for (const [key, value] of Object.entries({
      email: opt(email),
      phone: opt(phone),
      legalName: opt(legalName),
      ein: opt(ein),
      website: opt(website),
      niche: opt(niche),
      addressLine1: opt(addressLine1),
      addressLine2: opt(addressLine2),
      city: opt(city),
      state: opt(state),
      zip: opt(zip),
      timezone: opt(timezone),
    })) {
      if (value !== undefined) raw[key] = value;
    }

    const schema = mode === 'create' ? companyCreateSchema : companyUpdateSchema;
    const parsed = schema.safeParse(raw);
    if (parsed.success) {
      return { input: parsed.data as CompanyCreateInput | CompanyUpdateInput, errs: {} };
    }
    const errs: FieldErrors = {};
    for (const issue of parsed.error.issues) {
      const key = issue.path[0] as string | undefined;
      if (key && !errs[key]) errs[key] = issue.message;
    }
    return { input: {} as CompanyCreateInput, errs };
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const { input, errs } = buildPayload();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      if (mode === 'create') {
        await createMut.mutateAsync(input as CompanyCreateInput);
        toast.success('Company created');
      } else if (initial) {
        await updateMut.mutateAsync({ id: initial.id, patch: input as CompanyUpdateInput });
        toast.success('Changes saved');
      }
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // Duplicate slug — route or service returns 409 with 'slug' in the message.
      if (/slug/i.test(message) && /(conflict|already|409|unique)/i.test(message)) {
        setErrors({ slug: 'Slug already in use' });
      } else {
        setErrors({ form: "Couldn't save — try again" });
        toast.error("Couldn't save — try again");
      }
    } finally {
      setBusy(false);
    }
  }

  function field(
    id: string,
    label: string,
    value: string,
    setValue: (v: string) => void,
    props: { placeholder?: string; type?: string; required?: boolean } = {},
  ) {
    return (
      <div className="space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <Input
          id={id}
          type={props.type ?? 'text'}
          required={props.required}
          placeholder={props.placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-invalid={!!errors[id]}
        />
        {errors[id] && <p className="text-xs text-destructive">{errors[id]}</p>}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Create Company' : 'Edit Company'}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Just the basics — fill out the full business profile inside the company page.'
              : 'Update this company’s business profile.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {field('name', 'Business name', name, setName, { required: true })}
          {mode === 'create' && (
            <>
              {field('email', 'Main email', email, setEmail, { type: 'email' })}
              {field('phone', 'Main phone', phone, setPhone, { type: 'tel' })}
              {slug && (
                <p className="text-xs text-muted-foreground">
                  URL ID: <code>{slug}</code> (auto-generated)
                </p>
              )}
              {errors.slug && <p className="text-xs text-destructive">{errors.slug}</p>}
            </>
          )}

          {mode === 'edit' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {field('legalName', 'Legal business name', legalName, setLegalName)}
                {field('ein', 'EIN (optional)', ein, setEin, { placeholder: 'XX-XXXXXXX' })}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {field('email', 'Business email', email, setEmail, { type: 'email' })}
                {field('phone', 'Business phone', phone, setPhone, { type: 'tel' })}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {field('website', 'Website', website, setWebsite, {
                  placeholder: 'https://example.com',
                })}
                {field('niche', 'Niche / industry', niche, setNiche)}
              </div>

              <h4 className="text-sm font-medium pt-2">Address (United States)</h4>
              {field('addressLine1', 'Street address', addressLine1, setAddressLine1)}
              {field(
                'addressLine2',
                'Street address 2 (suite, unit…)',
                addressLine2,
                setAddressLine2,
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {field('city', 'City', city, setCity)}
                {field('state', 'State', state, setState)}
                {field('zip', 'ZIP code', zip, setZip)}
              </div>

              <div className="space-y-2">
                <Label htmlFor="timezone">Time zone</Label>
                <select
                  id="timezone"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  <option value="">Not set</option>
                  {US_TIMEZONES.map(({ tz, label }) => (
                    <option key={tz} value={tz}>
                      {label}
                    </option>
                  ))}
                </select>
                {errors.timezone && <p className="text-xs text-destructive">{errors.timezone}</p>}
                <p className="text-xs text-muted-foreground">
                  Times are stored in UTC and shown in this zone for the company.
                </p>
              </div>

              <h4 className="text-sm font-medium pt-2">Advanced</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {field('slug', 'Slug (URL ID)', slug, setSlug, { required: true })}
                {field(
                  'gaPropertyIdOverride',
                  'GA4 property ID override',
                  gaOverride,
                  setGaOverride,
                  {
                    placeholder: 'e.g. 312345678',
                  },
                )}
              </div>
            </>
          )}

          {errors.form && (
            <Alert variant="destructive">
              <AlertDescription>{errors.form}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
